import { Button, Input, InputRef, message, Popover, Table, TableProps, Tag } from "antd"
import { useEffect, useRef, useState } from "react";
import { deleteBrandRequest, getBrandRequests, getSuitableBrandRequests } from "../../../API/endpoints/Discovery";
import DiscoverBrandItemCell from "./components/DiscoverBrandItemCell";
import Paragraph from "antd/es/typography/Paragraph";
import dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY, STATUS_INTEREST_ACCEPTED, STATUS_INTEREST_EXPRESSED, STATUS_INTEREST_RECEIVED, STATUS_INTERESTS_EXPRESSED, STATUS_INTERESTS_RECEIVED, STATUS_OPEN, STATUS_PARTNER_INTEREST_ACCEPTED, STATUS_PARTNER_INTEREST_EXPRESSED, STATUS_PENDING } from "../../../constants/constants";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import DeleteModalSimple from "../../Organization/components/DeleteModalSimple";
import RequestMarketPartnerModal from "./components/RequestMarketPartnerModal";
import { useNavigate } from "react-router-dom";
import { FilterDropdownProps } from "antd/es/table/interface";
import SearchIconSmall from "../../../components/icons/SearchIconSmall";
import { c } from "framer-motion/dist/types.d-Cjd591yU";
import { discoverStatusListBrand, discoverStatusListPartner, productCategories } from "../../../constants/dataConstants";
import FilterIcon from "../../../components/icons/FilterIcon";
import SortIcon from "../../../components/icons/SortIcon";

const PAGE_SIZE = 20;

interface Props {
    userProfile: any
}

const Discovery = (props: Props) => {
    const { userProfile } = props;

    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();

    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [reload, setReload] = useState(false);
    const [page, setPage] = useState(1);

    const [isDeleteItem, setIsDeleteItem] = useState<any>({});
    const [isAddRequest, setIAddRequest] = useState(false);

    const [searchKey, setSearchKey] = useState("");
    const [filterStatuses, setFilterStatuses] = useState<string[]>([])
    const [filterCategories, setFilterCategories] = useState<string[]>([]);
    const [sorter, setSorter] = useState({ field: "", order: "" })

    const [scrollY, setScrollY] = useState(0);

    const tableRef = useRef<any>(null);
    const searchInput = useRef<InputRef>(null);




    const loadData = async (pageNumber: number) => {
        setLoading(true);
        if (userProfile?.org_id) {
            getBrandRequests(page, PAGE_SIZE, userProfile?.org_id, userProfile?.partnerv2_id, filterStatuses, filterCategories, searchKey, sorter?.order, sorter?.field).then((res) => {

                const newData = res?.data?.map((item: any) => {
                    const data = item?.discoveryRequest || item;
                    const status = item?.discoveryBrandPartner?.status && userProfile?.partnerv2_id == item?.discoveryBrandPartner?.partner_id ? item?.discoveryBrandPartner?.status : data?.status
                    const counts = item?.counts && item?.counts?.length > 0 ? item?.counts[0] : {}
                    return {
                        id: data?.id,
                        brand_name: data?.brand?.brand_name,
                        category: data?.category,
                        subCategory: data?.sub_category,
                        interested_count: counts?.interested_count || 0,
                        updatedAt: data?.updated_at,
                        createdAt: data?.created_at,
                        status: status,
                    }
                }) || []
                pageNumber == 1 ? setData(newData) : setData(prev => [...prev, ...newData]);
                const total = res?.totalCount || 0;
                setHasMore((pageNumber * PAGE_SIZE) < total);
                setLoading(false);
            }).catch((err) => {
                console.log("Failed to fetch brand requests", err);
                setLoading(false);
                setHasMore(false);
                setData([]);
            })

        } else if (userProfile?.partnerv2_id) {
            getSuitableBrandRequests(userProfile?.partnerv2_id, filterStatuses, filterCategories, searchKey, sorter?.order, sorter?.field).then((res) => {

                const newData = res?.data?.map((item: any) => {
                    const data = item?.discoveryRequest || item;
                    const status = item?.brand_partners && item?.brand_partners?.length > 0 ? item?.brand_partners[0]?.status : data?.status
                    const counts = item?.counts && item?.counts?.length > 0 ? item?.counts[0] : {}
                    return {
                        id: data?.id,
                        brand_name: data?.brand?.brand_name,
                        category: data?.category,
                        subCategory: data?.sub_category,
                        interested_count: counts?.interested_count || 0,
                        updatedAt: data?.updated_at,
                        createdAt: data?.created_at,
                        status: status,
                    }
                }) || []
                pageNumber == 1 ? setData(newData) : setData(prev => [...prev, ...newData]);
                setHasMore(false);
                setLoading(false);
            }).catch((err) => {
                console.log("Failed to fetch brand requests", err);
                setLoading(false);
                setHasMore(false);
                setData([]);
            })
        }
    };

    useEffect(() => {
        setPage(1);
        loadData(1);
    }, [searchKey, filterStatuses, filterCategories, sorter])

    useEffect(() => {
        loadData(page);
    }, [page, reload, userProfile]);


    useEffect(() => {
        const updateScrollY = () => {
            if (tableRef.current) {
                const containerHeight = tableRef.current.clientHeight;
                const headerHeight = 48; // Adjust this value if your header is bigger/smaller
                setScrollY(containerHeight - headerHeight);
            }
        };

        updateScrollY();
        window.addEventListener('resize', updateScrollY);
        return () => window.removeEventListener('resize', updateScrollY);
    }, []);

    const onCancelAdd = () => {
        setIAddRequest(false)
    }

    const onSuccessRequest = () => {
        setPage(1);
        setHasMore(true);
        setReload(!reload);
    }


    const onSuccessDelete = () => {
        messageApi.success("Discovery request deleted successfully");
        const newList = data.filter((item: any) => item?.id !== isDeleteItem?.id)
        setData([...newList])
        setIsDeleteItem({});
    }


    const onViewDetails = (id: string) => {
        navigate(userProfile?.partnerv2_id ? `/partner-user/discovery/${id}` : `/brand-user/discovery/${id}`)
    }


    const handleTableChange = (_: any, filters: any, sorter: any) => {

        const sortOrder = sorter?.order ? sorter?.order === "ascend" ? "ASC" : "DESC" : undefined

        setFilterStatuses(filters?.status || []);
        setFilterCategories(filters?.category || []);
        setSorter({ field: sorter?.field || "", order: sortOrder || "" })
    }


    const handleSearch = (selectedKeys: any, confirm: any) => {
        confirm();
        if (selectedKeys[0] != undefined) {
            setSearchKey(selectedKeys[0]?.trim());
        }
    };

    const handleReset = (clearFilters: any, confirm: any) => {
        clearFilters();
        setSearchKey("");
        confirm();
    };


    const getColumnSearchProps = (dataIndex: any) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            close,
        }: FilterDropdownProps) => (
            <div style={{ padding: 8 }}>
                <Input
                    classNames={{
                        input: "text-[12px]"
                    }}
                    ref={searchInput}
                    placeholder={`Search`}
                    value={selectedKeys[0]}
                    prefix={<SearchIconSmall />}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm)}
                    className="mb-[8px]"
                />
                <div className="flex gap-2 justify-end">
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, confirm)}
                        size="small"
                        className="text-[12px]"
                    >Reset</Button>
                    <Button
                        type="primary"
                        title="Search"
                        size="small"
                        className="text-[12px]"
                        onClick={() => handleSearch(selectedKeys as string[], confirm)}
                    >Search</Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchIconSmall color={filtered ? "#0152FB" : "#667085"} />
        ),
    });

    const columns: TableProps['columns'] = [
        {
            title: 'Brand Name',
            dataIndex: 'brand_name',
            key: 'brand_name',
            render: (brand: string, record: any) => (
                <DiscoverBrandItemCell data={record} />
            ),
            ...getColumnSearchProps("brand_name")
        },
        {
            title: 'Category',
            dataIndex: 'category',
            className: "font-[500]",
            key: 'category',
            filters: productCategories,
            filterIcon: (filtered: boolean) => (
                <FilterIcon filtered={filtered} />
            )
        },
        {
            title: 'Sub-Category',
            dataIndex: 'subCategory',
            key: 'subCategory',
            render: (items: string[]) => (
                items?.length > 2 ? (
                    <span>
                        {items?.slice(0, 2)?.map((item: string) => (
                            <Tag bordered={false}>{item}</Tag>
                        ))}
                        <Popover
                            trigger="hover"
                            placement="bottom"
                            content={(
                                <Paragraph className='!mb-0 pl-[12px]'>
                                    <ul className="!mb-0">
                                        {items?.map((item: any, index: number) => (
                                            <li className="text-[13px] font-[500] m-0" key={index}>
                                                {item}
                                            </li>
                                        ))
                                        }
                                    </ul>
                                </Paragraph>
                            )}  >
                            <Button type="link" size="small" className="!px-0 leading-[12px] text-[12px] underline">+{items?.length - 2} more</Button>
                        </Popover>
                    </span>

                ) : (
                    items?.map((item: string) => (
                        <Tag bordered={false}>{item}</Tag>
                    ))
                )
            )
        },
        {
            title: 'Int. Partners',
            dataIndex: 'interested_count',
            key: 'interested_count',
            className: "font-[500]",
            align: "center",
            width: 150,
            sorter: true,
            sortIcon: ({ sortOrder }) => <SortIcon order={sortOrder} />,
            hidden: userProfile?.partnerv2_id,
            render: (value: number) => (
                <>{value?.toString().padStart(2, "0")}</>
            )
        },
        {
            title: 'Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            className: "font-[500]",
            hidden: userProfile?.partnerv2_id,
            sorter: true,
            sortIcon: ({ sortOrder }) => <SortIcon order={sortOrder} />,
            render: (value: string) => (
                <>{value ? dayjs(value).format(DATE_FORMAT_DD_MM_YYYY) : ""}</>
            )
        },
        {
            title: 'Posted',
            dataIndex: 'createdAt',
            key: 'createdAt',
            className: "font-[500]",
            sorter: true,
            sortIcon: ({ sortOrder }) => <SortIcon order={sortOrder} />,
            hidden: !userProfile?.partnerv2_id,
            render: (value: string) => (
                <>{value ? dayjs(value).format(DATE_FORMAT_DD_MM_YYYY) : ""}</>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filters: userProfile?.partnerv2_id ? discoverStatusListPartner : discoverStatusListBrand,
            filterIcon: (filtered: boolean) => (
                <FilterIcon filtered={filtered} />
            ),
            render: (status: string) => {
                const statusText = status === STATUS_OPEN ? STATUS_PENDING : userProfile?.org_id && status === STATUS_INTEREST_EXPRESSED ? STATUS_INTEREST_RECEIVED : status;
                let color = "warning";
                if (statusText === STATUS_INTEREST_RECEIVED) {
                    color = "purple"
                } else if (statusText === STATUS_INTEREST_EXPRESSED || statusText === STATUS_PARTNER_INTEREST_EXPRESSED) {
                    color = "blue"
                } else if (statusText === STATUS_INTEREST_ACCEPTED || statusText === STATUS_PARTNER_INTEREST_ACCEPTED) {
                    color = "success"
                } else {
                    color = "warning"
                }

                return (statusText === STATUS_PENDING && userProfile?.partnerv2_id ? (
                    <></>
                ) : (<Tag
                    bordered={false}
                    className="capitalize !rounded-[24px] text-[10px] font-[500]"
                    color={color}
                >
                    {statusText?.replaceAll("_", " ")?.toLowerCase()}
                </Tag>
                )
                )
            }
        },
        {
            title: false,
            dataIndex: 'status',
            key: 'action',
            align: "end",
            render: (status: string, record: any) => (
                <div className="flex items-center gap-x-[8px] justify-end">
                    {status === STATUS_OPEN && !userProfile?.partnerv2_id ? (
                        <Button
                            icon={<DeleteIcon color="#98A2B3" />}
                            type="text"
                            className="leading-[12px]"
                            onClick={() => setIsDeleteItem(record)}
                        />
                    ) : (
                        <></>
                    )}

                    <Button
                        type="text"
                        className="leading-[13px] text-[13px] !text-[#0152FB] bg-[#EFF8FF] hover:!bg-[#D1E9FF] font-[500]"
                        onClick={() => onViewDetails(record?.id)}
                    >
                        View Details
                    </Button>
                </div>
            )
        },
    ];

    return (
        <div className="h-[calc(100vh-49px)] px-[24px] py-[16px]">
            {contextHolder}
            <div className="bg-white p-[16px] rounded-[8px] h-full">
                <div className="mb-[20px] flex items-center justify-between">
                    <div>
                        <h2 className="text-[13px] font-[500] mb-[4px]">Discovery Requests</h2>
                        <p className="text-[12px] text-[#475467]">Manage and track your market partner requests</p>
                    </div>
                    {Object.keys(userProfile)?.length > 0 && !userProfile?.partnerv2_id ? (
                        <Button type="primary" onClick={() => setIAddRequest(true)}>Request Market Partner</Button>
                    ) : (
                        <></>
                    )}

                </div>
                <div
                    ref={tableRef}
                    className='h-[calc(100%-48px)] overflow-auto overflow-scroll-bar'
                >
                    <Table
                        dataSource={data}
                        columns={columns}
                        size="small"
                        pagination={false}
                        rowKey="key"
                        loading={loading}
                        scroll={{ y: scrollY, x: "max-content" }}
                        onChange={handleTableChange}
                        onScroll={(e) => {
                            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                            if (!loading && hasMore && scrollHeight - scrollTop <= clientHeight + 100) {
                                setPage(prev => prev + 1);
                            }
                        }}
                    />
                </div>
            </div>
            <RequestMarketPartnerModal
                open={isAddRequest}
                onCancel={onCancelAdd}
                orgID={userProfile?.org_id}
                onSuccess={onSuccessRequest}
            />
            <DeleteModalSimple
                width={420}
                title={"Confirm Deletion"}
                content={`Are you sure you want to delete this discovery request? This action cannot be undone.`
                }
                okText={"Delete Request"}
                item={isDeleteItem}
                onCancel={() => setIsDeleteItem({})}
                onSuccess={() => onSuccessDelete()}
                api={deleteBrandRequest}
            />
        </div>
    )
}

export default Discovery